import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { IPageHeadline } from 'app/modules/shared/classes/page-title-parameters';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import { HierarchyFiltersCustomOptions } from 'app/modules/shared/components/main-filters/hierarchy-filter-options/hierarchy-flters-custom-options';
import { DisplayOptions, ISyncAsOfDateInf } from 'app/modules/shared/interfaces/display-options';
import {
    NoDataActionType,
    NoDataSourceType,
    NoDataTypes,
} from 'app/modules/shared/interfaces/no-data';
import { IHierachyFilterCacheUrlInfo } from 'app/modules/shared/interfaces/user-location';
import { FiltersService } from 'app/modules/shared/services/filters.service';
import { IEntityLeaderboardParams } from '../../interfaces/performance-entity-data';
import {
    IDataMappingProperties,
    IDataProperties,
    INavigationParameters,
    IWeightedScoreTable,
} from 'app/modules/performance/components/performance-insight-weighted-rankings/table/performance-insight-ranking-table.component';
import { EntityLeaderboardService } from 'app/modules/performance/services/entity-leaderboard.service';

@Component({
    selector: 'performance-branch-leaderboard',
    templateUrl: './performance-branch-leaderboard.component.html',
    styleUrls: ['./performance-branch-leaderboard.component.scss'],
})
export class PerformanceBranchLeaderboardComponent implements OnInit {
    displayOptions: DisplayOptions[];
    noDataType: NoDataTypes;
    hFilterJSON = '';
    pageHeadline: IPageHeadline = {
        module: 'performance',
        moduleIcon: 'fa-heartbeat',
        pageTitle: 'Branch Leaderboard by Weighted Score',
        linkArray: [
            {
                moduleLink: 'performance',
                linkTitle: 'Performance Home',
                showLink: true,
                isHome: true,
            },
            {
                moduleLink: 'performance/insights',
                linkTitle: 'Performance Insights',
                showLink: true,
            },
        ],
    };

    hierarchyCacheInfo: HierarchyFiltersCustomOptions = new HierarchyFiltersCustomOptions();
    hierarchyCachePageInfo: IHierachyFilterCacheUrlInfo;
    ymc: string;
    datePeriod: string;
    showTop: string;
    tablePageSize: number = 10;
    syncAsOfDateInf?: ISyncAsOfDateInf;
    navigationParams: INavigationParameters;
    dataMappingProperties: IDataMappingProperties;
    dataProperties: IDataProperties;
    leaderTables: IWeightedScoreTable[];

    constructor(
        private _reusableFunctions: ReusableFunctions,
        private _router: Router,
        private filterService: FiltersService,
        private _entityLeaderboardService: EntityLeaderboardService
    ) {}

    ngOnInit() {
        this.noDataType = {
            action: NoDataActionType.pageStart,
            searchTerm: '',
            componentTitle: this.pageHeadline.pageTitle,
            inputSource: NoDataSourceType.largeDataCard,
        };
        this.displayOptions = this._reusableFunctions.getCurrentDisplayOptions(
            '/performance/insights/branch-leaderboard'
        );

        this.dataProperties = {
            data: [],
            hasData: false,
            isLoading: true,
            tableTitle: '',
            roleId: '',
            tablePageSize: this.tablePageSize,
            showFooter: false,
            action: NoDataActionType.pageStart,
        };

        this.navigationParams = {
            titleRouterBase: '/performance/insights/',
            titleUrlSegment: 'performance-rankings-branch',
            cellRouterBase: '/performance/scorecards/',
            cellUrlSegment: 'branch',
            cellQueryParamsList: [
                { param: 'level', valueId: '', source: 'parent' },
                { param: 'id', valueId: 'hierarchyIdentifier', source: 'cell' },
            ],
        };

        this.dataMappingProperties = {
            unitsMappingProperty: 'totalUnits',
            volumeMappingProperty: 'totalVolume',
            scoreMappingProperty: 'totalScore',
            nameMappingProperty: 'hierarchyName',
        };

        this.hFilterJSON = this._reusableFunctions.getHierarchyFilters();
        this.setDisplayOptionsSelected();
        this.getFilterRoles();
    }

    getFilterRoles() {
        let url = this._router.url.split('?')[0];
        this.hierarchyCacheInfo = new HierarchyFiltersCustomOptions();
        this.hierarchyCachePageInfo = this.hierarchyCacheInfo.getHierarchyCachePageInfo(url);
        this.leaderTables = [];
        this.filterService.getUsersAllLocations(this.hierarchyCachePageInfo.employeeType).subscribe(
            (res) => {
                let empIdx = res.findIndex((role) => role.roleName == 'Employee');
                if (empIdx != -1) {
                    res.splice(empIdx, 1);
                }

                res.forEach((card) => {
                    let dataProperties = this.dataProperties;
                    let navigationParams = this.navigationParams;
                    dataProperties.roleId = card.roleName;
                    dataProperties.tableTitle = card.roleName;
                    dataProperties.tablePageSize = this.tablePageSize;
                    navigationParams.cellUrlSegment = 'branch';

                    let paramList = navigationParams.cellQueryParamsList.map((obj) => ({ ...obj }));
                    let queryParam = paramList.find(
                        (prm) => prm.source === 'parent' && prm.param === 'level'
                    );

                    if (queryParam) {
                        queryParam.valueId = card.roleName;
                    }
                    navigationParams.cellQueryParamsList = paramList;

                    let scoreTable: IWeightedScoreTable = Object.assign(
                        {},
                        {
                            dataProperties: Object.assign({}, dataProperties),
                            navigationParams: Object.assign({}, navigationParams),
                            dataMappingProperties: Object.assign({}, this.dataMappingProperties),
                        }
                    );
                    this.leaderTables.push(scoreTable);
                });

                this.getRankings();
            },
            (error) => {
                throw new Error(error);
            }
        );
    }

    getRankings() {
        let parameters: IEntityLeaderboardParams = {
            datePeriod: this.datePeriod,
            roleName: '',
            YMC: this.ymc,
            staff: '1',
            hFilterJSON: this.hFilterJSON,
        };

        this.leaderTables.forEach((scoreTable: IWeightedScoreTable) => {
            parameters.roleName = scoreTable.dataProperties.roleId;

            this._entityLeaderboardService.getEntityWeightedScore(parameters).subscribe(
                (data) => {
                    if (data && data.length > 0) {
                        scoreTable.dataProperties.data = data;
                        scoreTable.dataProperties.hasData = true;
                    } else {
                        scoreTable.dataProperties.hasData = false;
                    }
                    scoreTable.dataProperties.isLoading = false;
                    scoreTable.dataProperties = Object.assign({}, scoreTable.dataProperties);
                    scoreTable = Object.assign({}, scoreTable);
                },
                (error) => {
                    scoreTable.dataProperties.hasData = false;
                    scoreTable.dataProperties.isLoading = false;
                    scoreTable.dataProperties.action = NoDataActionType.error;
                    throw new Error(error);
                }
            );
        });
    }

    applyDisplayOptions(event: DisplayOptions[]) {
        this.displayOptions = event;
        this.setNoDataType(NoDataActionType.filter);
        this.setDisplayOptionsSelected();
        this.getFilterRoles();
    }

    setDisplayOptionsSelected() {
        let asOfDateId = 'scorecardAsOf';
        let dateRangeId = 'dateRangeRolling_12m_ytd_lm-mtd';
        this.syncAsOfDateInf = {
            asOfDateId: asOfDateId,
            syncWithDisplayOptionId: dateRangeId,
        };

        let asOfInfo = this.displayOptions.find((item) => item.optionKey === asOfDateId);
        if (asOfInfo) {
            this.ymc = asOfInfo.selectedOptions;
        }

        let dateRangeInfo = this.displayOptions.find((item) => item.optionKey === dateRangeId);
        if (dateRangeInfo) {
            this.datePeriod = dateRangeInfo.selectedOptions;
        }

        let showTopInfo = this.displayOptions.find((item) => item.optionKey === 'showTop');
        if (showTopInfo) {
            this.showTop = showTopInfo.selectedOptions;
            this.assignGroupBy();
        }
    }

    assignGroupBy(): void {
        let topNumber = this.showTop.replace('Top ', ''); // Remove prepended text of option
        this.tablePageSize = +topNumber;
    }

    applyNewFilters(event: string) {
        this.hFilterJSON = event;
        this.setNoDataType(NoDataActionType.filter);
        this.getFilterRoles();
    }

    setNoDataType(action: NoDataActionType) {
        this.noDataType.action = action;
    }
}
